"use client";

import { useQuery } from "@tanstack/react-query";
import { getCompanyProfile } from "@/api/company-profile";
import { CLUTCH_RATING } from "@/lib/testimonials";

const SITE_URL = "https://www.exec9.com";

// Static fallback values, used only until the company-profile query resolves (or if it fails) so the
// JSON-LD is never empty on first paint. Once the admin dashboard data loads, everything below is
// driven by it — kept in sync with the footer, which reads the same react-query cache (same queryKey).
const FALLBACK = {
  companyName: "Exec9",
  logoUrl: `${SITE_URL}/logo.svg`,
  // Crawlers read the server-rendered fallback, not the resolved query — an empty address here
  // drops `address` from the Organization entity for every bot that never runs the client fetch.
  address: "29-A, Block A, Valencia Town, Lahore, 54770, Pakistan",
  email: "info@exec9.com",
  phone: "+92-3028490697",
  social: [
    "https://www.instagram.com/exec9",
    "https://www.facebook.com/exec9",
    "https://www.linkedin.com/company/exec9",
    "https://clutch.co/profile/exec9",
  ],
} as const;

// Maps a country name (as free-typed in the admin panel) to its ISO 3166-1 alpha-2 code. Defaults to
// PK since Exec9 is Lahore-based; extend as needed if the profile ever names another country.
const COUNTRY_CODES: Record<string, string> = {
  pakistan: "PK",
  pk: "PK",
};

function toPostalAddress(address: string) {
  const parts = address
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return undefined;

  let addressCountry = "PK";
  const maybeCountry = parts[parts.length - 1]?.toLowerCase();
  if (maybeCountry && COUNTRY_CODES[maybeCountry]) {
    addressCountry = COUNTRY_CODES[maybeCountry];
    parts.pop();
  }

  let postalCode: string | undefined;
  let addressLocality: string | undefined;
  const postalIdx = parts.findIndex((p) => /^[\d][\d\s-]*$/.test(p));
  if (postalIdx !== -1) {
    postalCode = parts[postalIdx];
    if (postalIdx > 0) addressLocality = parts[postalIdx - 1];
    parts.splice(postalIdx > 0 ? postalIdx - 1 : postalIdx, postalIdx > 0 ? 2 : 1);
  } else if (parts.length > 1) {
    // no postal code — treat the last remaining segment as the city
    addressLocality = parts.pop();
  }

  const streetAddress = parts.join(", ");

  return {
    "@type": "PostalAddress",
    ...(streetAddress ? { streetAddress } : {}),
    ...(addressLocality ? { addressLocality } : {}),
    ...(postalCode ? { postalCode } : {}),
    addressCountry,
  };
}

export function OrganizationJsonLd() {
  const { data } = useQuery({
    queryKey: ["company-profile"],
    queryFn: getCompanyProfile,
    staleTime: 5 * 60 * 1000,
  });

  const companyName = data?.companyName || FALLBACK.companyName;
  const url = data?.website || SITE_URL;
  const logo = data?.logoUrl || FALLBACK.logoUrl;
  const email = data?.email || FALLBACK.email;
  const phone = data?.phone || FALLBACK.phone;
  const address = data?.address || FALLBACK.address;

  // The admin profile models social links only, so its values are merged with — never substituted
  // for — the fallbacks, which carry the Clutch profile that verifies the site's own review claims.
  const sameAs = [
    ...new Set([...(data?.socialLinks ? Object.values(data.socialLinks) : []), ...FALLBACK.social].filter(Boolean)),
  ];

  // @id is the site's canonical entity node: every other block (LocalBusiness, Review, JobPosting,
  // ProfessionalService) points at this same IRI, so engines resolve one Exec9 rather than seven
  // look-alike organisations across seven pages.
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: companyName,
    url,
    logo,
    description:
      "Exec9 is a full-cycle software development agency based in Lahore, Pakistan, building AI-powered web, mobile, and blockchain products for clients worldwide across Retail, E-Commerce, Fintech, Healthcare, and Education.",
    ...(sameAs.length > 0 ? { sameAs } : {}),
    address: toPostalAddress(address),
    telephone: phone,
    email,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      telephone: phone,
      email,
      areaServed: "Worldwide",
      availableLanguage: ["English", "Urdu"],
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: CLUTCH_RATING.ratingValue,
      bestRating: "5",
      worstRating: "1",
      reviewCount: CLUTCH_RATING.reviewCount,
    },
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: companyName,
    url,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organization) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(website) }}
      />
    </>
  );
}
